/**
 * Phase 3 — 첨부 파일을 자료함에 저장하자는 제안.
 * 순수 헬퍼: 파일 목록에서 재사용 가치가 있는 첫 파일을 고르고 카드 payload 를 만든다.
 */

// "학기"/"연간" 단독은 "2학기 답안.pdf" 같은 일회성 파일까지 잡으므로 운영·계획·일정 등과 붙을 때만 본다.
export const LIBRARY_REUSE_HEURISTIC = /평가계획서|계획서|양식|서식|템플릿|교육과정|성취기준|기준표|루브릭|채점기준|진도표|교과서|지도서|학교\s*규정|학사일정|(?:연간|학기)\s*(?:운영|계획|일정|지도|교육)/;

const MAX_FILE_ID_LENGTH = 200;

const toIdSet = (value) => {
  if (value instanceof Set) {
    return new Set([...value].map((item) => String(item || '').trim()).filter(Boolean));
  }
  if (Array.isArray(value)) {
    return new Set(value.map((item) => String(item || '').trim()).filter(Boolean));
  }
  return new Set();
};

export const getLibraryFileId = (file) => String(
  file?.serverFileId || file?.id || ''
).trim();

export const collectUserMessageFiles = (message) => {
  if (!message || typeof message !== 'object') return [];
  const raw = message.attachments || message.uploadedFiles;
  return Array.isArray(raw) ? raw.filter(Boolean) : [];
};

export const isAnalyzedRegularUpload = (file) => {
  if (!file || typeof file !== 'object') return false;
  if (file.isReferenceMaterial) return false;
  if (file.isGenerated || file.generated || file.isImageMode) return false;
  if (
    file.source === 'generated'
    || file.source === 'image_generation'
    || file.source === 'design_step_upload'
  ) {
    return false;
  }
  if (file.attachmentPurpose === 'design_reference') return false;
  if (file.error || file.queued || file.uploading) return false;
  const analyzed = file.uploaded === true
    || file.extractionStatus === 'success'
    || file.analysisStatus === 'complete'
    || file.analysisComplete === true
    || file.status === '✓';
  if (!analyzed) return false;
  return Boolean(getLibraryFileId(file));
};

export const matchesLibraryReuseHeuristic = (file) => {
  const haystack = [
    file?.name,
    file?.filename,
    file?.original_filename,
    file?.type,
    file?.file_type,
    file?.mime_type,
  ].filter(Boolean).join(' ');
  return LIBRARY_REUSE_HEURISTIC.test(haystack);
};

// 자료함 자동 모으기가 켜진 대화: 문서는 서버가 알아서 담으므로 카드를 띄우지 않고,
// 학생 정보로 보여 자동으로 담지 않은 파일만 "이 파일도 담을까요?"로 묻는다.
export const isSkippedAsSensitive = (file) => (
  String(file?.libraryAutoCollect?.reason || '').startsWith('sensitive_')
);

export const pickLibrarySuggestionFile = (files, { savedIds, dismissedIds, autoCollect } = {}) => {
  if (!Array.isArray(files)) return null;
  const saved = toIdSet(savedIds);
  const dismissed = toIdSet(dismissedIds);
  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    if (!isAnalyzedRegularUpload(file)) continue;
    const fileId = getLibraryFileId(file);
    if (saved.has(fileId) || dismissed.has(fileId)) continue;
    if (autoCollect) {
      if (!isSkippedAsSensitive(file)) continue;
    } else if (!matchesLibraryReuseHeuristic(file)) {
      continue;
    }
    return file;
  }
  return null;
};

export const buildLibrarySuggestion = (file, { isProUser, autoCollect } = {}) => {
  const fileId = getLibraryFileId(file);
  if (!fileId) return null;
  const fileName = String(
    file?.name || file?.filename || file?.original_filename || ''
  ).trim();
  if (isProUser && autoCollect) {
    return {
      id: `file_to_library:${fileId}`,
      type: 'file_to_library',
      title: '학생 정보가 보여서 자료함에 자동으로 담지 않았어요',
      description: '필요한 자료라면 직접 담을 수 있어요.',
      primary: { label: '이 파일도 담기', action: 'save_to_library' },
      secondary: { label: '괜찮아요', action: 'dismiss' },
      payload: { fileId, fileName, isProUser: true },
    };
  }
  // 4050 가이드: 질문형("~할까요?") 대신 정보형. 기억 제안 카드("~저장할 수 있어요")와 같은 톤.
  if (isProUser) {
    return {
      id: `file_to_library:${fileId}`,
      type: 'file_to_library',
      title: '이 파일을 자료함에 저장할 수 있어요',
      description: '다음 채팅에서 첨부 없이 바로 꺼내 쓸 수 있어요.',
      primary: { label: '자료함에 저장', action: 'save_to_library' },
      secondary: { label: '나중에', action: 'dismiss' },
      payload: { fileId, fileName, isProUser: true },
    };
  }
  // Premium Gap: 혜택 먼저, PRO 안내는 한 줄. "나중에"는 항상 함께 둔다(Respect the No).
  return {
    id: `file_to_library:${fileId}`,
    type: 'file_to_library',
    title: '자료함에 저장하면 다음 채팅에서 첨부 없이 바로 꺼내 쓸 수 있어요',
    description: 'PRO 플랜에서 쓸 수 있는 기능이에요.',
    primary: { label: 'PRO 알아보기', action: 'upgrade' },
    secondary: { label: '나중에', action: 'dismiss' },
    payload: { fileId, fileName, isProUser: false },
  };
};

export const findLatestCompletedAssistantTurn = (messages) => {
  if (!Array.isArray(messages)) return null;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const candidate = messages[index];
    if (candidate?.role === 'user') continue;
    if (candidate?.role !== 'assistant') continue;
    // 최신 답변이 아직 스트리밍·오류면 이전 답변에 카드를 남기지 않는다.
    if (candidate.streaming || candidate.error) return null;
    let user = null;
    for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
      if (messages[cursor]?.role === 'user') {
        user = messages[cursor];
        break;
      }
    }
    return { assistant: candidate, user, assistantIndex: index };
  }
  return null;
};

/**
 * 카드에 쓸 파일 후보. 우선순위:
 *  1) 그 턴의 사용자 메시지에 attachments/uploadedFiles 가 있으면 그것
 *  2) 이 세션에서 전송한 스냅샷 { userContent, files } — 사용자 본문이 같을 때만
 *     (메시지 id 는 Firestore 재구독 시 바뀌므로 본문으로 묶는다)
 *  3) 없으면 [] — 새로고침 후 세션 첨부 목록으로 폴백하지 않는다.
 *     오래된 첨부가 무관한 최신 답변 아래 다시 튀어나오는 "맥락 없는 제안"을 막기 위함.
 */
export const resolveLibrarySuggestionTurnFiles = (turn, snapshot) => {
  if (!turn?.assistant) return [];
  const messageFiles = collectUserMessageFiles(turn.user);
  if (messageFiles.length > 0) return messageFiles;
  if (!snapshot || !Array.isArray(snapshot.files)) return [];
  const userContent = String(turn.user?.content ?? '');
  return snapshot.userContent === userContent ? snapshot.files : [];
};

export const isPersistableLibraryFileId = (fileId) => {
  const normalized = String(fileId || '').trim();
  return Boolean(normalized) && normalized.length <= MAX_FILE_ID_LENGTH;
};

export const normalizeDismissedLibrarySuggestionFileIds = (value) => (
  Array.isArray(value)
    ? [...new Set(
      value
        .map((item) => String(item || '').trim())
        .filter(isPersistableLibraryFileId)
    )]
    : []
);
